import {
  withStyles,
  Select,
  TextField,
  DateRangePicker,
  AVAILABLE_TIMEZONES,
  objectEntries,
  InterestTerm,
  TransactionType,
  InterestAccountTransactionsRequest,
} from 'vopay';
import type { InterestAccountTransactionHistoryStatus, Timestamp, Timezone } from 'vopay';
import { useMemo, useState } from 'react';
import { format, subWeeks } from 'date-fns';
import styles from './SearchBar.module.scss';
import { useClientData } from '@/providers/ClientData.provider';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';
import { Button, TableFiltersContainer } from 'UI';
import { useTranslation } from 'react-i18next';
import useFileDownload from '@/hooks/useFiledDownload';

const cn = withStyles(styles);

type TransactionTypeCA =
  | TransactionType.EFT_WITHDRAWAL
  | TransactionType.EFT_FUNDING
  | TransactionType.VOPAYINSTANT_WITHDRAWAL
  | TransactionType.REFUND
  | TransactionType.CURRENCY_CONVERSION
  | TransactionType.INTERACT_MONEY_REQUEST
  | TransactionType.INTERACT_BULK_PAYOUT
  | TransactionType.INBOUND_E_TRANSFER
  | TransactionType.VISA_DIRECT_PUSH
  | TransactionType.CREDIT_CARD
  | TransactionType.GOOGLE_PAY
  | TransactionType.PAYPAL_WITHDRAWAL
  | TransactionType.ACCOUNT_TRANSFER
  | TransactionType.REVERSAL
  | TransactionType.BILL_PAY
  | TransactionType.OTHER_FUNDING
  | TransactionType.OTHER_WITHDRAWAL
  | TransactionType.AUTO_BALANCE_TRANSFER
  | TransactionType.VIRTUAL_ACCOUNT_DEPOSIT
  | TransactionType.VIRTUAL_ACCOUNT_FUNDING
  | TransactionType.VIRTUAL_ACCOUNT_WITHDRAW
  | TransactionType.VIRTUAL_ACCOUNT_WIRE;

const typeOptionsCA: Record<TransactionTypeCA, string> = {
  'EFT Withdrawal': 'EFT Withdrawal',
  'EFT Funding': 'EFT Funding',
  'VoPayInstant Withdraw': 'VoPayInstant Withdraw',
  Refund: 'Refund',
  'Currency Conversion': 'Currency Conversion',
  'Interac Money Request': 'Interac Money Request',
  'Interac Bulk Payout': 'Interac Bulk Payout',
  'Inbound e-Transfer': 'Inbound e-Transfer',
  'Visa Direct Push': 'Visa Direct Push',
  'Credit Card': 'Credit Card',
  'Google Pay': 'Google Pay',
  'PayPal Withdrawal': 'PayPal Withdrawal',
  'Account Transfer': 'Account Transfer',
  Reversal: 'Reversal',
  'Bill Pay': 'Bill Pay',
  'Auto Balance Transfer': 'Auto Balance Transfer',
  'Virtual Account Deposit': 'Virtual Account Deposit',
  'Virtual Account Funding': 'Virtual Account Funding',
  'Virtual Account Withdraw': 'Virtual Account Withdraw',
  'Virtual Account Wire': 'Virtual Account Wire',
  'Other Funding': 'Other Funding',
  'Other Withdrawal': 'Other Withdrawal',
};

type TransactionTypeUS =
  | TransactionType.ACH_WITHDRAWAL
  | TransactionType.ACH_FUNDING
  | TransactionType.RTP_WITHDRAWAL
  | TransactionType.FEDWIRE_WITHDRAWAL
  | TransactionType.FEDWIRE_FUNDING
  | TransactionType.REFUND
  | TransactionType.CURRENCY_CONVERSION
  | TransactionType.VISA_DIRECT_PUSH
  | TransactionType.CREDIT_CARD
  | TransactionType.GOOGLE_PAY
  | TransactionType.PAYPAL_WITHDRAWAL
  | TransactionType.VENMO_WITHDRAWAL
  | TransactionType.ACCOUNT_TRANSFER
  | TransactionType.REVERSAL
  | TransactionType.AUTO_BALANCE_TRANSFER
  | TransactionType.VIRTUAL_ACCOUNT_DEPOSIT
  | TransactionType.VIRTUAL_ACCOUNT_FUNDING
  | TransactionType.VIRTUAL_ACCOUNT_WITHDRAW
  | TransactionType.VIRTUAL_ACCOUNT_WIRE
  | TransactionType.OTHER_FUNDING
  | TransactionType.OTHER_WITHDRAWAL;

const typeOptionsUS: Record<TransactionTypeUS, string> = {
  'ACH Withdrawal': 'ACH Withdrawal',
  'ACH Funding': 'ACH Funding',
  'RTP Withdrawal': 'RTP Withdrawal',
  'Fedwire Withdrawal': 'Fedwire Withdrawal',
  'Fedwire Funding': 'Fedwire Funding',
  Refund: 'Refund',
  'Currency Conversion': 'Currency Conversion',
  'Visa Direct Push': 'Visa Direct Push',
  'Credit Card': 'Credit Card',
  'Google Pay': 'Google Pay',
  'PayPal Withdrawal': 'PayPal Withdrawal',
  'Venmo Withdrawal': 'Venmo Withdrawal',
  'Account Transfer': 'Account Transfer',
  Reversal: 'Reversal',
  'Auto Balance Transfer': 'Auto Balance Transfer',
  'Virtual Account Deposit': 'Virtual Account Deposit',
  'Virtual Account Funding': 'Virtual Account Funding',
  'Virtual Account Withdraw': 'Virtual Account Withdraw',
  'Virtual Account Wire': 'Virtual Account Wire',
  'Other Funding': 'Other Funding',
  'Other Withdrawal': 'Other Withdrawal',
};

type SearchFormProps = {
  hasLongTermAccount: boolean;
  disabled: boolean;
  tableID: string;
  onSearch: (searchParams: InterestAccountTransactionsRequest) => void;
};

export default function InterestAccountTransactionSearchForm({
  hasLongTermAccount,
  disabled,
  tableID,
  onSearch,
}: SearchFormProps) {
  const { t } = useTranslation('translations', {
    keyPrefix: 'Accounts.InterestAccountsDrawer.TransactionsTab',
  });
  const { t: tDrawer } = useTranslation('translations', { keyPrefix: 'Accounts.InterestAccountsDrawer' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { userSettings } = useUserSettingsProvider();
  const { response } = useClientData();
  const [transactionStatus, setTransactionStatus] = useState<InterestAccountTransactionHistoryStatus | undefined>();
  const [timezone, setTimezone] = useState<Timezone | undefined>(userSettings.timezone);
  const [transactionIDSearchTerm, setTransactionIDSearchTerm] = useState('');
  const [interestMaturityType, setInterestMaturityType] = useState<InterestTerm | undefined>();
  const [timeRange, setTimeRange] = useState<[Timestamp, Timestamp]>([
    subWeeks(new Date(), 1).getTime() as Timestamp,
    new Date().getTime() as Timestamp,
  ]);
  const [transactionType, setTransactionType] = useState<TransactionTypeCA | TransactionTypeUS | undefined>();
  const { downloadCsvFile } = useFileDownload();

  const transactionTypeOptions = useMemo<
    {
      value: TransactionTypeCA | TransactionTypeUS;
      label: string;
    }[]
  >(() => {
    if (response.Country === 'US') {
      return objectEntries(typeOptionsUS).map(([value, label]) => ({
        value,
        label: tGlobal(`TransactionTypes.${label}`),
      }));
    }

    return objectEntries(typeOptionsCA).map(([value, label]) => ({
      value,
      label: tGlobal(`TransactionTypes.${label}`),
    }));
  }, [response.Country]);

  const statusOptions: Record<InterestAccountTransactionHistoryStatus, string> = {
    waiting: 'waiting',
    pending: 'pending',
    successful: 'successful',
  };

  const statusOptionsArray = Object.keys(statusOptions).map((status) => ({
    value: status,
    label: tGlobal(`Statuses.${status}`),
  }));

  const accountTypeOptions: Record<InterestTerm, string> = {
    'short-term': tDrawer('standard'),
    'long-term': tDrawer('premium'),
  };

  const accountTypeOptionsArray = objectEntries(accountTypeOptions).map(([value, label]) => ({
    value,
    label,
  }));

  const timezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const initialTimeZones = AVAILABLE_TIMEZONES.map((tz) => ({
    label: tz,
    value: tz,
  })).filter((tz) => !tz.value.toLowerCase().includes('utc') && !tz.value.includes(timezoneName));

  const timeZoneOptions = [
    {
      label: 'UTC',
      value: 'UTC',
    },
    {
      label: timezoneName,
      value: timezoneName,
    },
    ...initialTimeZones,
  ];

  function handleSearch() {
    const startDate = format(new Date(timeRange[0]), 'yyyy-MM-dd') || '';
    const endDate = format(new Date(timeRange[1]), 'yyyy-MM-dd') || '';

    onSearch({
      TransactionType: transactionType,
      InterestMaturityType: interestMaturityType,
      TransactionStatus: transactionStatus,
      Timezone: timezone,
      TransactionID: transactionIDSearchTerm,
      StartDate: startDate,
      EndDate: endDate,
    });
  }

  return (
    <TableFiltersContainer
      filtersApplied={!!interestMaturityType || !!transactionIDSearchTerm || !!transactionStatus || !!transactionType}
    >
      <div
        style={{
          marginLeft: 'auto',
          display: 'flex',
          alignItems: 'center',
          gap: 16,
        }}
      >
        <TableFiltersContainer.Trigger />

        <Button
          size="small"
          disabled={disabled}
          onClick={() => downloadCsvFile(tableID, t('accountTransaction', { defaultValue: 'Account Transaction' }))}
        >
          {tGlobal('ExportDropdown.label')}
        </Button>
      </div>

      <TableFiltersContainer.Content>
        <form
          className={cn('searchBar')}
          onSubmit={(e) => {
            e.preventDefault();
            handleSearch();
          }}
        >
          <DateRangePicker
            triggerVariant="outlineShadow"
            disabled={disabled}
            onChange={(range) => {
              if (range && range[0] && range[1]) {
                setTimeRange(range);
              }
            }}
            initialTime={timeRange}
          />

          <Select
            version="2"
            disabled={disabled}
            placeholder={t('TransactionType.placeholder', { defaultValue: 'Trans. Type' })}
            options={transactionTypeOptions}
            name="type"
            value={transactionType}
            onBlur={() => {}}
            showClear
            onChange={(e) => {
              setTransactionType(e.target.value as TransactionTypeCA | TransactionTypeUS);
            }}
          />

          {hasLongTermAccount && (
            <Select
              version="2"
              disabled={disabled}
              placeholder={t('AccountType.placeholder', { defaultValue: 'Acc. Type' })}
              options={accountTypeOptionsArray}
              name="status"
              value={interestMaturityType}
              onBlur={() => {}}
              showClear
              onChange={(_, { data }) => {
                if (data) {
                  setInterestMaturityType(data.value);
                } else {
                  setInterestMaturityType(undefined);
                }
              }}
            />
          )}

          <Select
            version="2"
            disabled={disabled}
            placeholder={t('Status.placeholder', { defaultValue: 'Status' })}
            options={statusOptionsArray}
            name="status"
            value={transactionStatus}
            onBlur={() => {}}
            showClear
            onChange={(e) => {
              setTransactionStatus(e.target.value as InterestAccountTransactionHistoryStatus);
            }}
          />

          <Select
            version="2"
            disabled={disabled}
            placeholder={t('Timezone.placeholder', { defaultValue: 'Timezone' })}
            options={timeZoneOptions}
            name="timeZone"
            value={timezone}
            onBlur={() => {}}
            filterable
            showClear
            onChange={(e) => {
              setTimezone(e.target.value as Timezone);
            }}
            sort={false}
          />

          <div className={cn('lastGroup')}>
            <TextField
              key="TransactionSearchKeyword"
              name="TransactionSearchKeyword"
              disabled={disabled}
              placeholder={t('TransactionID.placeholder', { defaultValue: 'Trans. ID' })}
              value={transactionIDSearchTerm}
              onBlur={() => {}}
              onChange={(e) => {
                setTransactionIDSearchTerm(e.target.value);
              }}
              variant="outlineShadow"
            />

            <Button
              size="small"
              disabled={disabled}
              colorScheme="primary"
              className={cn('searchButton')}
              id="search-transaction-history"
              type="submit"
            >
              {tGlobal('Buttons.search')}
            </Button>
          </div>
        </form>
      </TableFiltersContainer.Content>
    </TableFiltersContainer>
  );
}
